// src/components/LieferscheinPacken.tsx
import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArtikelThumbnail from './ArtikelThumbnail';
import { fetchApi } from './fetchApi';


const ArtikelThumbnailAny = ArtikelThumbnail as any;

// --------------------------
// Interfaces
// --------------------------
interface Position {
  id: number;
  artikel_id: number;
  menge: number;
  sku: string;
  bezeichnung: string;

  // neu: Thumbnail
  bild_url?: string | null;
}

interface Lieferschein {
  id: number;
  lieferschein_nr: string;
  status: string;
  erstellt_am: string;
  quelle_name: string;
  empfaenger: string;
  bemerkung: string | null;

  // Für Lagerort-Bestimmung beim Verbuchen
  quelle_typ: string;
  quelle_id: number | null;

  positionen: Position[];
}

interface BoxEintrag {
  artikel_id: number;
  boxnummer: string;
  menge: number;
  lagerort_id: number;

  regalnummer?: string | null;
  fachnummer?: string | null;
}

// --------------------------
// Hauptkomponente
// --------------------------
export default function LieferscheinPacken() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [daten, setDaten] = useState<Lieferschein | null>(null);
  const [boxen, setBoxen] = useState<BoxEintrag[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  // Entnahme-Form: { [position_id]: { [boxnummer]: menge } }
  const [entnahmen, setEntnahmen] = useState<Record<number, Record<string, number>>>({});

  // --------------------------
  // Hilfsfunktion: Ist-Menge je Position
  // --------------------------
  function berechneIstMenge(positionId: number): number {
    const posEntnahme = entnahmen[positionId];
    if (!posEntnahme) return 0;

    return Object.values(posEntnahme).reduce((sum, val) => {
      const n = Number(val);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }

  // --------------------------
  // Daten laden
  // --------------------------
  useEffect(() => {
    async function lade() {
      try {
        const lsRes = await fetchApi(`/api/lieferschein/view/${id}`);
        const lsJson = await lsRes.json();
        if (!lsRes.ok) throw new Error('Fehler beim Laden des Lieferscheins');
        setDaten(lsJson);

        const boxRes = await fetchApi('/api/view/lagerstand-pro-box');
        const boxJson = await boxRes.json();
        setBoxen(Array.isArray(boxJson) ? boxJson : []);

     } catch (err: any) {
  console.error(err);
  setFehler(
    err?.message === '401'
      ? 'Session abgelaufen. Bitte neu einloggen.'
      : 'Fehler beim Laden der Daten.'
  );
} finally {
  setLaden(false);
}

    }

    lade();
  }, [id]);

  // --------------------------
  // Entnahme ändern
  // --------------------------
  function handleEntnahmeChange(
    positionId: number,
    boxnummer: string,
    value: number
  ) {
    setEntnahmen(prev => ({
      ...prev,
      [positionId]: {
        ...(prev[positionId] || {}),
        [boxnummer]: value
      }
    }));
  }

  // --------------------------
  // Gesamte Entnahme speichern
  // --------------------------
  async function handleSpeichern() {
    if (!daten) return;

    if (!daten.quelle_id) {
      alert('Fehler: Kein Lagerort (Quelle) für diesen Lieferschein definiert.');
      return;
    }

    const payload: any[] = [];

    for (const pos of daten.positionen) {
      const posEntnahme = entnahmen[pos.id];
      if (!posEntnahme) continue;

      for (const boxnummer of Object.keys(posEntnahme)) {
        const menge = posEntnahme[boxnummer];
        if (menge > 0) {
          payload.push({
            lieferschein_position_id: pos.id,
            artikel_id: pos.artikel_id,
            boxnummer,
            menge,
            lagerort_id: daten.quelle_id
          });
        }
      }
    }

    if (payload.length === 0) {
      alert('Keine Entnahmen eingegeben.');
      return;
    }

    // --------------------------------------
    // Clientseitige Soll/Ist-Prüfung wie im Backend:
    // Summe Entnahmen pro Position MUSS == Soll-Menge sein
    // --------------------------------------
    const entnahmeSummen = new Map<number, number>();

    for (const ent of payload) {
      const bisher = entnahmeSummen.get(ent.lieferschein_position_id) || 0;
      entnahmeSummen.set(ent.lieferschein_position_id, bisher + ent.menge);
    }

    const fehlerLines: string[] = [];
    for (const pos of daten.positionen) {
      const ist = entnahmeSummen.get(pos.id) || 0;
      const soll = pos.menge;
      if (ist !== soll) {
        fehlerLines.push(`${pos.sku} – Soll: ${soll}, Ist: ${ist}`);
      }
    }

    if (fehlerLines.length > 0) {
      alert(
        'Die Entnahmen stimmen noch nicht mit den Soll-Mengen überein:\n\n' +
        fehlerLines.join('\n')
      );
      return;
    }

    try {
      const res = await fetchApi(
  `/api/lieferschein/${daten.id}/verbuchen`,
  {
    method: 'POST',
    body: JSON.stringify({ entnahmen: payload })
  }
);


      const json = await res.json();
      if (!res.ok) {
  alert(
    res.status === 401
      ? 'Session abgelaufen. Bitte neu einloggen.'
      : (json.error || 'Fehler beim Speichern.')
  );
  return;
}

      alert('Entnahmen erfolgreich verbucht.');
      navigate(`/lieferschein/${daten.id}`);

    } catch (err) {
      console.error(err);
      alert('Fehler beim Speichern.');
    }
  }

  // --------------------------
  // Ladeanzeige
  // --------------------------
  if (laden) {
    return (
      <Container>
        <Box mt={5} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (fehler || !daten) {
    return (
      <Container>
        <Box mt={4}>
          <Alert severity="error">{fehler || 'Keine Daten gefunden.'}</Alert>
        </Box>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/lieferschein-liste')}>
          Zurück
        </Button>
      </Container>
    );
  }

  // --------------------------
  // Gesamtfortschritt berechnen
  // --------------------------
  const gesamtSoll = daten.positionen.reduce((sum, pos) => sum + pos.menge, 0);
  const gesamtIst = daten.positionen.reduce(
    (sum, pos) => sum + berechneIstMenge(pos.id),
    0
  );
  const allesFertig = gesamtIst === gesamtSoll && gesamtSoll > 0;

  // --------------------------
  // UI
  // --------------------------
  return (
    <Container sx={{ mt: 4, mb: 10 }}>
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Typography variant="h5">
          Lieferschein packen – {daten.lieferschein_nr}
        </Typography>

        <Button variant="outlined" onClick={() => navigate(`/lieferschein/${daten.id}`)}>
          Zurück
        </Button>
      </Box>

      <Typography variant="subtitle1">
        Sender: <strong>{daten.quelle_name}</strong>
      </Typography>
      <Typography variant="subtitle1" sx={{ mt: 1 }}>
        Empfänger: <strong>{daten.empfaenger}</strong>
      </Typography>
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Erstellt am: {new Date(daten.erstellt_am).toLocaleString('de-AT')}
      </Typography>

      {daten.bemerkung && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Bemerkung: {daten.bemerkung}
        </Typography>
      )}

      {/* Gesamtfortschritt */}
      <Box mt={3} mb={2}>
        <Alert severity={allesFertig ? 'success' : 'info'}>
          Gesamt gepackt: <strong>{gesamtIst}</strong> / <strong>{gesamtSoll}</strong>
        </Alert>
      </Box>

      <Typography variant="h6" sx={{ mt: 4 }}>
        Positionen & Boxen
      </Typography>

      {daten.positionen.map((pos) => {
        // Ist / Rest je Position
        const istMenge = berechneIstMenge(pos.id);
        const restMenge = pos.menge - istMenge;
        const istFertig = restMenge === 0;
        const istFehler = restMenge < 0;

        // Nur Boxen dieses Artikels UND dieses Lagerorts (Sender)
        // UND nur Boxen mit Bestand > 0 (keine leeren Boxen anzeigen)
        const boxenFuerArtikel = boxen
          .filter(
            (b) =>
              b.artikel_id === pos.artikel_id &&
              b.lagerort_id === daten.quelle_id &&
              b.menge > 0
          )
          // Beste zuerst: Box mit der größten Menge oben
          .sort((a, b) => b.menge - a.menge);

        return (
          <Box
            key={pos.id}
            sx={{
              mt: 3,
              mb: 4,
              p: 2,
              border: '1px solid',
              borderColor: istFehler ? 'red' : istFertig ? 'green' : '#ddd',
              borderRadius: 1,
              backgroundColor: istFertig
                ? '#f0fff0'
                : istFehler
                ? '#fff0f0'
                : 'transparent'
            }}
          >
            {/* Kopfzeile: Thumbnail + SKU + Titel (eine Zeile) */}
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <ArtikelThumbnailAny
                src={pos.bild_url || null}
                url={pos.bild_url || null}
                alt={pos.sku}
                size={28}
              />
              <Typography variant="subtitle1">
                {pos.sku} – {pos.bezeichnung}
              </Typography>
            </Box>

            <Typography variant="body2">
              Soll: <strong>{pos.menge}</strong>{' '}
              | Ist: <strong>{istMenge}</strong>{' '}
              | Rest: <strong>{restMenge < 0 ? 0 : restMenge}</strong>
            </Typography>

            {istFehler && (
              <Typography variant="body2" sx={{ mt: 0.5, color: 'red' }}>
                Achtung: Entnahmen überschreiten die Soll-Menge!
              </Typography>
            )}

            {boxenFuerArtikel.length === 0 ? (
              <Typography variant="body2" sx={{ mt: 1, color: 'red' }}>
                Keine Bestände für diesen Artikel im gewählten Sender-Lagerort gefunden.
              </Typography>
            ) : (
              <Table sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Regal</TableCell>
                    <TableCell>Fach</TableCell>
                    <TableCell>Box</TableCell>
                    <TableCell align="right">Verfügbar</TableCell>
                    <TableCell align="right">Entnehmen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boxenFuerArtikel.map((b) => (
                    <TableRow key={`${b.lagerort_id}-${b.boxnummer}`}>
                      <TableCell>{b.regalnummer ?? '-'}</TableCell>
                      <TableCell>{b.fachnummer ?? '-'}</TableCell>
                      <TableCell>{b.boxnummer}</TableCell>
                      <TableCell align="right">{b.menge}</TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>
                        <TextField
                          type="number"
                          fullWidth
                          size="small"
                          inputProps={{ min: 0, max: b.menge }}
                          value={entnahmen[pos.id]?.[b.boxnummer] ?? ''}
                          onChange={(e) =>
                            handleEntnahmeChange(
                              pos.id,
                              b.boxnummer,
                              Number(e.target.value)
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        );
      })}

      <Box mt={4}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSpeichern}
          disabled={!allesFertig}
        >
          Entnahmen speichern
        </Button>
      </Box>
    </Container>
  );
}
